import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    Switch,
    Tooltip,
    Typography,
} from "@mui/material";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";

import { RetryImage } from "../../../components/RetryImage";

import { Input } from "../../../components/Input";
import { deleteFile } from "../../../apis/fileUpload";
import {
    createOption,
    getOptionByID,
    updateOption,
} from "../../../apis/option";
import { useGlobalSnackBar } from "../../SnackbarContext";
import { SelectTag } from "../../../components/Tag/SelectTab";
import { useModelData } from "../../../hook/useModelData";
import { useOptionData } from "../../../hook/useOptionData";
import { SelectComponent } from "../../../components/Select";
import {
    transformOptions,
    transformOriginalOptions,
    Option,
} from "../../../utils/option";
import { Loader } from "../../../components/Loader";
import { CustomDialog } from "../../../components/modal/CustomDialog";

import "./styles.css";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { useBreadcrumb } from "../../../context/BreadcrumbContext";

const transformCategoryData = (data: any) => {
    const transformNode = (node: any) => {
        const transformedNode: any = {
            value: node.id,
            label: node.text,
        };

        if (node.items && node.items.length > 0) {
            transformedNode.items = node.items.map(transformNode);
        }

        return transformedNode;
    };

    return data.map(transformNode);
};

export const OptionsView: React.FC = () => {
    const { ID } = useParams();
    const navigate = useNavigate();

    const [option, setOption] = useState<any>(null);
    const [modelTags, setModelTags] = useState<any[]>([]);
    const [optionTags, setOptionTags] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [logos, setLogos] = useState<Array<string | ArrayBuffer | null>>([
        null,
        null,
        null,
    ]);
    const [isLoading, setLoading] = useState<boolean>(true);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [imageData, setImageData] = useState<{ logo: any; index: number }>();
    const [isEnabled, setIsEnabled] = useState<boolean>(false);
    const [isDimensionsEnabled, setIsDimensionsEnabled] = useState<boolean>(false);
    const { showSnackBar } = useGlobalSnackBar();
    const currentUser = useSelector((state: RootState) => state.adminAuth.admin);
    const { setDetail, clearDetail } = useBreadcrumb();

    const { models } = useModelData();

    const { options } = useOptionData();

    useEffect(() => {
        return () => clearDetail();
    }, [clearDetail]);

    useEffect(() => {
        const fetchOption = async () => {
            try {
                const optionData = await getOptionByID({ ID });
                setOption({ ...optionData });
                setLogos(optionData.images ? JSON.parse(optionData.images) : []);
                setModelTags(
                    optionData.applicable_model_ids
                        ? JSON.parse(optionData.applicable_model_ids)
                        : []
                );
                setOptionTags(
                    optionData.uncompatible_option_ids
                        ? JSON.parse(optionData.uncompatible_option_ids)
                        : []
                );
                setIsEnabled(optionData?.isQTYEnabled)
                setIsDimensionsEnabled(optionData?.isDimensionsEnabled)
                if (optionData.option_name) {
                    setDetail(optionData.option_name, "/options");
                }
                setLoading(false);
            } catch (error) {
                console.error("Failed to fetch option", error);
                showSnackBar("Server error", "error");
            }
        };

        if (ID) {
            fetchOption();
        } else {
            setLoading(false);
        }
    }, [ID]);

    useEffect(() => {
        const orderedOptionData = transformOriginalOptions(
            transformOptions(options, true),
            true
        );
        const _orderedOptionData = orderedOptionData?.filter(
            (option: Option) => option.is_category
        );

        setCategoryOptions(transformCategoryData(_orderedOptionData));
    }, [options]);

    const permissionErrorMessage = () => {
        showSnackBar("Invalid operation: Only Administrators can make this change.", "error");
    };

    const handleUploadClick = (
        event: React.ChangeEvent<HTMLInputElement>,
        index: number
    ) => {
        if (currentUser?.permission !== "Administrator") {
            permissionErrorMessage();
            return;
        }

        const file = event.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("img", file);

        axios
            .post(process.env.REACT_APP_UPLOAD_URL || "", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: "Bearer " + localStorage.getItem("admin_access_token"),
                },
            })
            .then((response) => {
                const { filename } = response.data;
                setLogos((prevLogos) => {
                    const updatedLogos = [...prevLogos];
                    updatedLogos[index] = filename;
                    return updatedLogos;
                });
            })
            .catch((error) => {
                console.error("Error uploading file:", error);
            });
    };

    const handleRemoveClick = async (
        logo: string | ArrayBuffer | null,
        index: number
    ) => {
        if (currentUser?.permission !== "Administrator") {
            permissionErrorMessage();
            return;
        }

        if (!logo) return;
        setOpenModal(true);
        setImageData({ logo: logo, index: index });
    };

    const handleDelete = async () => {
        if (imageData?.logo) {
            await deleteFile({ file: logos[imageData.index] });
            setLogos((prevLogos) => {
                const updatedLogos = [...prevLogos];
                updatedLogos[imageData.index] = null;
                return updatedLogos;
            });
            setOpenModal(false);
        }
    };

    const handleSubmit = async (event: any) => {
        event.preventDefault();

        if (currentUser?.permission !== "Administrator") {
            permissionErrorMessage();
            return;
        }

        const formData = new FormData(event.target);
        const formValues: any = Object.fromEntries(formData.entries());
        formValues["images"] = JSON.stringify(logos);
        formValues["ID"] = ID;
        formValues["uncompatible_option_ids"] = JSON.stringify(optionTags);
        formValues["applicable_model_ids"] = JSON.stringify(modelTags);
        formValues["isQTYEnabled"] = isEnabled ? true : false;
        formValues["isDimensionsEnabled"] = isDimensionsEnabled ? true : false;

        for (const key in formValues) {
            if (formValues[key] === "") {
                delete formValues[key];
            }
        }
        if (ID) {
            try {
                await updateOption(formValues);
                showSnackBar("Updated this option!");
                navigate("/options");
            } catch (err: any) {
                showSnackBar(err?.response?.data?.message || "Failed to update option", "error");
            }
        } else {
            try {
                await createOption(formValues);
                showSnackBar("Created new option!");
                navigate("/options");
            } catch (err: any) {
                showSnackBar(err?.response?.data?.message || "Failed to create option", "error");
            }
        }
    };

    const _orderedOptionData = useMemo(() => {
        return transformOriginalOptions(transformOptions(options, true), true);
    }, [options]);

    const orderedOptionData = useMemo(() => {
        return _orderedOptionData?.filter((option: Option) => !option.is_category);
    }, [_orderedOptionData]);

    if (isLoading) return <Loader isLoading={isLoading} />;

    return (
        <Box
            sx={{ maxWidth: 720, mx: "auto", py: 3, px: 2 }}
            component="form"
            onSubmit={handleSubmit}
        >
            {/* Basic Info */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Basic Info
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Input
                        label="Option Name"
                        size="medium"
                        name="option_name"
                        type="text"
                        defaultValue={option?.option_name || ""}
                    />
                    <Input
                        type="text"
                        name="part_number"
                        label="BF Part #"
                        size="medium"
                        defaultValue={option?.part_number || ""}
                    />
                </Box>
            </Paper>

            {/* Image */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Image
                </Typography>
                {(() => {
                    const imageUrl = typeof logos[0] === "string"
                        ? `${process.env.REACT_APP_COMPANY_LOGO_URL}/${logos[0]}`
                        : undefined;

                    return (
                        <RetryImage
                            sx={{ width: "100%", maxWidth: 300, height: 200 }}
                            variant="square"
                            src={imageUrl}
                            onClick={() => handleRemoveClick(logos[0], 0)}
                            className="uploadImage"
                        >
                            <input
                                accept="image/*"
                                id={`contained-button-file-${0}`}
                                type="file"
                                onChange={(e) => handleUploadClick(e, 0)}
                                style={{ display: "none" }}
                            />
                            <label htmlFor={`contained-button-file-${0}`}>
                                <IconButton component="span">
                                    <AddIcon />
                                </IconButton>
                            </label>
                        </RetryImage>
                    );
                })()}
            </Paper>

            {/* Pricing */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Pricing
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Input
                            label="MSRP"
                            name="MSRP"
                            defaultValue={option?.MSRP || ""}
                            type="number"
                            inputProps={{
                                step: "any",
                                min: "0",
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Input
                            label={`${process.env.REACT_APP_DEALER_NAME} Price`}
                            name="dealer_price"
                            defaultValue={option?.dealer_price || ""}
                            type="number"
                            inputProps={{
                                step: "any",
                                min: "0",
                            }}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Dimensions */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="h6">
                        Dimensions
                    </Typography>
                    <Tooltip title={isDimensionsEnabled ? "Disable Dimensions" : "Enable Dimensions"}>
                        <div>
                            <Switch
                                checked={isDimensionsEnabled}
                                onChange={(e) => {
                                    setIsDimensionsEnabled(e.target.checked);
                                }}
                            />
                        </div>
                    </Tooltip>
                </Box>
                {isDimensionsEnabled && (
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Input
                                label="Height"
                                name="demention_height"
                                defaultValue={option?.demention_height || ""}
                                type="number"
                                inputProps={{
                                    step: "any",
                                    min: "0",
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">"</InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Input
                                label="Width"
                                name="demention_width"
                                defaultValue={option?.demention_width || ""}
                                type="number"
                                inputProps={{
                                    step: "any",
                                    min: "0",
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">"</InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Input
                                label="Depth"
                                name="demention_depth"
                                defaultValue={option?.demention_depth || ""}
                                type="number"
                                inputProps={{
                                    step: "any",
                                    min: "0",
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">"</InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Input
                                label="Rack Spaces"
                                defaultValue={option?.demention_rack_spaces || ""}
                                name="demention_rack_spaces"
                                type="number"
                                inputProps={{
                                    step: "any",
                                    min: "0",
                                }}
                            />
                        </Grid>
                    </Grid>
                )}
            </Paper>

            {/* Category & QTY */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Category & QTY
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <SelectComponent
                            label="Category"
                            width="200"
                            defaultValue={option?.parent_id}
                            name="parent_id"
                            menuItems={categoryOptions}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Tooltip title={isEnabled ? "Disable QTY" : "Enable QTY"}>
                                <div>
                                    <Switch
                                        checked={isEnabled}
                                        onChange={(e) => {
                                            setIsEnabled(e.target.checked);
                                        }}
                                    />
                                </div>
                            </Tooltip>
                            {isEnabled && (
                                <Input
                                    defaultValue={option?.QTY || ""}
                                    name="QTY"
                                    width={150}
                                    type="number"
                                    inputProps={{
                                        step: "any",
                                        min: "1",
                                    }}
                                    label="Max QTY"
                                />
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Applies To */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Applies To
                </Typography>
                <Button variant="outlined" sx={{ borderRadius: "18px", lineHeight: "22px", mb: 2 }} onClick={() => setModelTags(models?.map(model => model.ID))}>
                    +all models
                </Button>
                <SelectTag
                    tags={modelTags}
                    setTags={setModelTags}
                    availableTags={models}
                />
            </Paper>

            {/* Not Compatible With */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Not Compatible With
                </Typography>
                <SelectTag
                    tags={optionTags}
                    setTags={setOptionTags}
                    availableTags={orderedOptionData}
                    currentID={ID}
                />
            </Paper>

            {/* Description */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Description
                </Typography>
                <Input
                    name="description"
                    rows={5}
                    defaultValue={option?.description || ""}
                />
            </Paper>

            {/* Save Button */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 4 }}>
                <Button sx={{ width: "150px" }} variant="contained" type="submit">
                    Save
                </Button>
            </Box>

            <CustomDialog
                title="Are you sure you want to change this image?"
                openModal={openModal}
                handleClose={() => setOpenModal(false)}
                handleAction={handleDelete}
                buttonTitle={"Yes"}
                children={undefined}
            />
        </Box>
    );
};
